import { useState } from 'react'

import { AppReview } from '@/api/app-client'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/use-toast'
import {
  useMutationApproveAppReview,
  useMutationRejectAppReview
} from '@/services/app.service'

type PendingAction = 'approve' | 'reject' | null

export const AppReviewActions = ({ review }: { review: AppReview }) => {
  const [pendingAction, setPendingAction] = useState<PendingAction>(null)

  const approveReview = useMutationApproveAppReview()
  const rejectReview = useMutationRejectAppReview()

  if (review.review_status !== 'pending') {
    return null
  }

  const isMutating = approveReview.isPending || rejectReview.isPending

  const handleConfirm = () => {
    const mutation = pendingAction === 'approve' ? approveReview : rejectReview
    const action = pendingAction

    mutation.mutate(
      {
        appId: review.app_id,
        version: review.version,
        orgId: review.org_id
      },
      {
        onSuccess: () => {
          toast({
            title: action === 'approve' ? 'Review approved' : 'Review rejected',
            description:
              action === 'approve'
                ? `${review.app_id} v${review.version} will be published shortly.`
                : `${review.app_id} v${review.version} was rejected. The developer can submit a new review request.`
          })
          setPendingAction(null)
        },
        onError: (err) => {
          toast({
            title: 'Error',
            description: `Error while ${
              action === 'approve' ? 'approving' : 'rejecting'
            } the review. ${err instanceof Error ? err.message : ''}`
          })
          setPendingAction(null)
        }
      }
    )
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <Button
          disabled={isMutating}
          onClick={(e) => {
            e.stopPropagation()
            setPendingAction('approve')
          }}
          size="sm"
        >
          Approve
        </Button>
        <Button
          disabled={isMutating}
          onClick={(e) => {
            e.stopPropagation()
            setPendingAction('reject')
          }}
          size="sm"
          variant="destructive"
        >
          Reject
        </Button>
      </div>
      <Dialog
        onOpenChange={(open) => {
          if (!open) setPendingAction(null)
        }}
        open={pendingAction !== null}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-primary">
              {pendingAction === 'approve' ? 'Approve review' : 'Reject review'}
            </DialogTitle>
            <DialogDescription>
              {pendingAction === 'approve' ? (
                <>
                  Approve <b>{review.app_id}</b> version <b>{review.version}</b>
                  ? The version becomes publicly available to all organizations.
                </>
              ) : (
                <>
                  Reject <b>{review.app_id}</b> version <b>{review.version}</b>?
                  The review request is deleted and the developer can submit a
                  new one.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              disabled={isMutating}
              onClick={() => setPendingAction(null)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              disabled={isMutating}
              onClick={handleConfirm}
              variant={pendingAction === 'reject' ? 'destructive' : 'default'}
            >
              {isMutating
                ? 'Working…'
                : pendingAction === 'approve'
                ? 'Approve'
                : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
